import {
	type CustomerDto,
	type CustomerModelDTO,
	type UpdateUser,
	type User,
	CustomerControllerV3ApiFactory,
	UserControllerV3ApiFactory,
} from "@eu.mdotm.api/sphere";
import { getApiGen, makeController } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { eventBus } from "$root/event-bus";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { seconds } from "$root/utils";
import debounce from "debounce";
import { accessToken$ } from "../session/atom";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { getCurrentUser } from ".";
import { publicAxiosInstance } from "$root/api/config";

function userToRequiredUpdateUser(user: User): Required<UpdateUser> {
	return {
		email: user.email ?? "",
		name: user.name ?? "",
		surname: user.surname ?? "",
		enabled: true,
		customerId: user.customerId ?? "",
		role: user.role ?? "",
		roles: user.roles ?? [],
		services: user.services ?? [],
		importFormats: user.importFormats ?? [],
		exportFormats: user.exportFormats ?? [],
		importEnabled: user.importEnabled ?? false,
		exportEnabled: user.exportEnabled ?? false,
		importDirectory: user.importDirectory ?? "",
		decimalPlaces: user.decimalPlaces ?? 2,
	};
}

const { newQuery, newAction, invalidate } = repositoryHelperFactory("user");

const companyApi = () => getApiGen(CustomerControllerV3ApiFactory);
const userV3Api = () => getApiGen(UserControllerV3ApiFactory);
const customerV3Api = () => getApiGen(CustomerControllerV3ApiFactory);
const userV3ApiLoggedOut = () => makeController(UserControllerV3ApiFactory, publicAxiosInstance);

export class MissingTokenError extends Error {}

export const userRepository = {
	userList: newQuery("userList", () => axiosExtract(userV3Api().listAllUsers())),

	info: newQuery(
		"info",
		(accessToken?: string | null) =>
			accessToken
				? axiosExtract(
						userV3ApiLoggedOut().infoUser({
							headers: { Authorization: `Bearer ${accessToken}` },
						}),
					)
				: Promise.reject(new MissingTokenError()),
		{ retry: 0 },
	),

	// Like `info`, but for the impersonation flow: keeps the impersonator's own
	// bearer token and adds the `X-Impersonate-Email` header so the backend
	// resolves the request as the target user. Goes through the logged-out
	// instance (explicit headers) so it doesn't depend on interceptor timing.
	infoImpersonated: newQuery(
		"infoImpersonated",
		(accessToken: string, impersonateEmail: string) =>
			axiosExtract(
				userV3ApiLoggedOut().infoUser({
					headers: {
						Authorization: `Bearer ${accessToken}`,
						"X-Impersonate-Email": impersonateEmail,
					},
				}),
			),
		{ retry: 0 },
	),

	createUser: newAction(async (params: User) => {
		const response = await axiosExtract(userV3Api().createUser(params));
		invalidate(userRepository.userList.partialQueryKey);
		return response;
	}),

	updateUser: newAction(async (userId: string, params: UpdateUser) => {
		const response = await axiosExtract(userV3Api().updateUser(userId, params));
		invalidate(userRepository.userList.partialQueryKey);
		if (userId === getCurrentUser().id) {
			invalidate(userRepository.info.getQueryConfig(readAtom(accessToken$)).queryKey);
		}
		invalidate([userId]);
		return response;
	}),

	patchUser: newAction(async (userId: string, params: Partial<UpdateUser>) => {
		const latestUser = await userRepository.info.fetchQuery(readAtom(accessToken$));
		const mergedParams: Required<UpdateUser> = { ...userToRequiredUpdateUser(latestUser), ...params };
		const response = await axiosExtract(userV3Api().updateUser(userId, mergedParams));
		invalidate(userRepository.userList.partialQueryKey);
		if (userId === getCurrentUser().id) {
			invalidate(userRepository.info.getQueryConfig(readAtom(accessToken$)).queryKey);
		}
		invalidate([userId]);
		return response;
	}),

	createCustomer: newAction(async (params: CustomerDto) => {
		const response = await axiosExtract(customerV3Api().createCustomerWithDto(params));
		invalidate(userRepository.customerList.partialQueryKey);
		return response;
	}),

	updateCustomer: newAction(async (params: CustomerModelDTO) => {
		const response = await axiosExtract(customerV3Api().updateCustomer(params));
		await Promise.all([
			invalidate(userRepository.customerList.partialQueryKey, { async: true }),
			invalidate([params.id], { async: true }),
			invalidate(userRepository.customerIntegrationApiKey.partialQueryKey, { async: true }),
		]);
		return response;
	}),

	retrieveServicesType: newQuery("retrieveServicesType", () => axiosExtract(customerV3Api().retrieveServicesType())),

	customerList: newQuery("customerList", () => axiosExtract(companyApi().listAllCustomer1())),
	customer: newQuery("customer", (customerId: string) => axiosExtract(companyApi().getCustomerById(customerId))),
	/** Whether a webhook API key is stored for the customer. The key itself is never returned. ROOT only. */
	customerIntegrationApiKey: newQuery("customerIntegrationApiKey", (customerId: string) =>
		axiosExtract(customerV3Api().getIntegrationApiKeyExistence(customerId)),
	),
	customReportData: newQuery("customReportData", () => axiosExtract(userV3Api().customReportData())),
} as const;

eventBus.addListener(
	"user-update",
	debounce(() => {
		invalidate();
	}, seconds(2)),
);
